import CalendarConfig from '../../../../consts/CalendarConfig';
import { BODY_SILHOUETTE_VIEWBOX } from '../../../../components/bodySilhouette/bodySilhouetteFigures';
import { FREE_POINT_LABEL, regionAtPoint, regionLabel } from '../../../../components/bodySilhouette/bodyRegions';

/**
 * Marcadores do mapa corporal: criação, rótulos e preparo para a silhueta.
 *
 * Marcador gravado: { view, x, y (frações do quadro, 0 a 1), region (chave do
 * catálogo ou ''), side ('left' | 'right' | null), type, intensity (0 a 10 ou
 * null), note }. O número é a posição na lista do atendimento (1, 2, 3...).
 */

export const MARKER_TYPES = Object.freeze([
  { key: 'complaint', label: 'Queixa' },
  { key: 'observation', label: 'Observação' },
  { key: 'assessed', label: 'Área avaliada' },
]);

export const DEFAULT_MARKER_TYPE = MARKER_TYPES[0].key;

export const INTENSITY_OPTIONS = Object.freeze(Array.from({ length: 11 }, (_, index) => index));

const round4 = (value) => Math.round(value * 10000) / 10000;

export function markerTypeLabel(type) {
  const found = MARKER_TYPES.find((item) => item.key === type);
  return found ? found.label : type;
}

const baseMarker = (view, x, y, region, type) => ({
  view,
  x: round4(x),
  y: round4(y),
  region: region ? region.key : '',
  side: region ? region.side : null,
  type: type || DEFAULT_MARKER_TYPE,
  intensity: null,
  note: '',
});

/** Região sob um ponto em frações do quadro (ou null fora das zonas). */
export function regionAtFraction(view, x, y) {
  const { width, height } = BODY_SILHOUETTE_VIEWBOX;
  return regionAtPoint(view, x * width, y * height);
}

/** Texto mostrado junto ao cursor antes do clique. */
export function regionLabelAtFraction(view, x, y) {
  const region = regionAtFraction(view, x, y);
  return region ? region.label : FREE_POINT_LABEL;
}

/** Marcador em um ponto clicado (frações do quadro), com a região da zona sugerida. */
export function createMarkerAtPoint(view, x, y, type) {
  return baseMarker(view, x, y, regionAtFraction(view, x, y), type);
}

/** Marcador no ponto de referência de uma região (caminho sem clique). */
export function createMarkerForRegion(region, type) {
  const { width, height } = BODY_SILHOUETTE_VIEWBOX;
  return baseMarker(region.view, region.x / width, region.y / height, region, type);
}

/**
 * Marcador reposicionado por arrasto. A região acompanha o novo ponto se
 * ainda era a sugerida; uma região escolhida à mão é mantida.
 */
export function moveMarker(marker, x, y) {
  const suggested = regionAtFraction(marker.view, marker.x, marker.y);
  const keepRegion = Boolean(marker.region) && (!suggested || marker.region !== suggested.key);
  const moved = { ...marker, x: round4(x), y: round4(y) };
  if (keepRegion) return moved;
  const region = regionAtFraction(marker.view, x, y);
  return { ...moved, region: region ? region.key : '', side: region ? region.side : null };
}

export function hasIntensity(marker) {
  return marker.intensity !== null && marker.intensity !== undefined && marker.intensity !== '';
}

export function markerTitle(marker) {
  return regionLabel(marker.view, marker.region);
}

/** "Queixa · 7/10" */
export function markerSummary(marker) {
  const parts = [markerTypeLabel(marker.type)];
  if (hasIntensity(marker)) parts.push(`${marker.intensity}/10`);
  return parts.join(' · ');
}

export function formatDate(value) {
  return value ? CalendarConfig.GetDDMMAAAA(value) : '';
}

/** Registros (um por atendimento) do mais recente ao mais antigo. */
export function sortByDateDesc(records) {
  return [...records].sort((a, b) => {
    const byDate = String(b.markedAt || '').localeCompare(String(a.markedAt || ''));
    return byDate !== 0 ? byDate : Number(b.id) - Number(a.id);
  });
}

export function findOwnRecord(records, appointmentId) {
  if (appointmentId == null) return null;
  return records.find((record) => String(record.appointmentId) === String(appointmentId)) || null;
}

/**
 * Marcadores de uma vista no formato da silhueta. `prefix` diferencia as
 * chaves de cada origem (atendimento atual, anteriores); `muted` desenha
 * os anteriores esmaecidos, sem halo, com a data no título do balão.
 * `highlightedIndex` destaca o marcador cujo item da lista está sob o mouse.
 */
export function toFigureMarkers(markers, view, { prefix, selectedIndex = null, highlightedIndex = null, muted = false, title = null } = {}) {
  return markers
    .map((marker, index) => ({ marker, index }))
    .filter(({ marker }) => marker.view === view)
    .map(({ marker, index }) => ({
      key: `${prefix}-${index}`,
      x: marker.x,
      y: marker.y,
      number: index + 1,
      type: marker.type,
      intensity: !muted && hasIntensity(marker) ? Number(marker.intensity) : null,
      muted,
      selected: selectedIndex === index,
      highlighted: highlightedIndex === index,
      title: muted ? `${title || 'Atendimento anterior'} · ${markerTitle(marker)}` : `${index + 1}. ${markerTitle(marker)}`,
      details: [markerSummary(marker), marker.note].filter(Boolean),
    }));
}
